import React from "react";
import { connect } from "react-redux";
import language from "../../../language/index.js";
import requestQuery from "../../../utils/queries/requestQuery.js";
import { Separator } from "../../Separator";
import moment from "moment";
import Products from "./products";
import capitalizeText from "../../../utils/capitalizeText.js";
import toastMessage from "../../../utils/toastMessage.js";
import Select from "../../Input/Select.js";
import formatSelectData from "../../../utils/formatSelectData.js";
import LabResults from "../../Patient/lab-results/index.js";
import { fetchPatientLabResult } from "../../../utils/queries/patient-lab-result.js";
import { fetchRequestedProducts } from "../../../utils/queries/request-product.js";
import { getStorage } from "../../../utils/storage.js";
import ForwardedList from "./forward/List";
import Input from "../../Input/Input.js";
import Button from "../../Button/Button.js";
import { defineRole } from "../../../utils/handleAccessRoles.js";
import {
  ROLE_HOEMOVIGILANCE,
  ROLE_LABORATORY,
  ROLE_NOMINATIVE_REQUEST,
// eslint-disable-next-line no-unused-vars
  ROLE_SAMPLE_COLLECTED_RESULT,
} from "../../../constants/ROLES.js";
import api from "../../../utils/api/index.js";
import Loading from "../../Loader/Loading.js";
import ControlledTabs from "../../Tabs/Tabs.js";
import icons from "../../../constants/icons.js";
import { ConfirmationModal, Modal } from "../../Modal/index.js";
import BookingTimeEditor from "../BookingTimeEditor";
import NewRequest from "./NewRequest.js";
import DirectTransfer from "../../../components/Requests/shipping";
import accountQuery from "../../../utils/queries/accountQuery";
import hospitalQuery from "../../../utils/queries/hospitalQuery";
import ServedProducts from "./served/List.js";
import Endorsement from "../emergency/Endorsement";
import PostReleaseResults from "../emergency/PostReleaseResults";
import { isEmergencyRelease, hasNoSample } from "../emergency/helpers";
import { fetchEmergencyOptions } from "../../../utils/queries/emergency.js";

class RequestInfo extends React.Component {
  state = {
    error: {},
    // The tick-list labels and the two statements, fetched once for the
    // panels that display them. Null until it arrives, and the panels are
    // written to render without it rather than flashing empty.
    emergencyOptions: null,
    distributors: [],
    data: [],
    csvData: [],
    sampleCollected: {},
    testedRequired: [],
    user: {},
    sampleCollectionDate: "",
    sampleCollectedBy: "",
    page: 1,
    limit: 50,
    showToConfirm: false,
    showBloodGroupMismatch: false,
    productData: [],
    editSampleCollected: false,
    editingBloodGroup: false,
    productsDummy: [],
    productsRefreshKey: 0,
    labResults: [],
  
    bloodGroup: null,
    sampleCollector: null,
    // cascade fields for sample blood group determination
    sampleAbo: null,
    sampleRhesus: null,
    sampleLiss: null,
    sampleIat: null,
    sampleCc: null,
};

  componentDidMount = async () => {
    this.getEmergencyOptions();

    const user = await getStorage();
    this.setState({ user });
    this.getRequestInfo(true);
  };

  componentDidUpdate(nextProps) {
    if (this.props.isRefreshing !== nextProps.isRefreshing) {
      this.getRequestInfo(true);
    }
  }

  getAccounts = async (isFetchingAccount, search = {}) => {
    try {
      this.setState({
        isFetchingAccount,
      });

      const data = await accountQuery(this.props.defaultLanguage, {
        type: "dropdown",
        page: 1,
        limit: 10,
        ...search,
      });

      this.setState({
        isFetchingAccount: false,
      });

      return data;
    } catch (error) {
      this.setState({ isFetchingAccount: false });
    }
  };

  onChangeText(field, e) {
    let { error = {} } = this.state;
    delete error[field];

    this.setState({
      [field]: e && e?.target ? e?.target?.value : e,
      error,
    });
  }

  onEditSampleCollected() {
    this.setState({
      editSampleCollected: !this.state.editSampleCollected,
    });
  }

  returnFilters() {
    const { requestId } = this.props;
    return {
      id: requestId,
    };
  }

  getRequestInfo = async (isLoading) => {
    try {
      const { defaultLanguage, requestNo } = this.props;

      this.setState({ isLoading });

      const { data } = await requestQuery(defaultLanguage, {
        requestNo,
      });

      const allowedTestedRequired = [];
      data?.[0]?.products?.forEach((el) => {
        for (let pEl of el.allowedTestRequired || []) {
          if (!allowedTestedRequired.includes(pEl)) {
            allowedTestedRequired.push(pEl);
          }
        }
      });

      this.setState({
        data,
        isLoading: false,
        allowedTestedRequired,
        ...(data?.[0] || {}),
        sampleCollectionDate:
          data?.length > 0
            ? data[0].sampleCollectionDate
              ? moment(data[0].sampleCollectionDate).format("YYYY-MM-DD HH:mm")
              : ""
            : "",
        sampleCollector: data[0]?.sampleCollectedBy
          ? {
              label: data[0].sampleCollectedBy?.name,
              value: data[0].sampleCollectedBy?._id,
            }
          : "",
        bloodGroup: data[0]?.sampleBloodGroup
          ? {
              label: data[0].sampleBloodGroup,
              value: data[0]?.sampleBloodGroup,
            }
          : "",
        ...this.parseSampleBloodGroup(data[0]?.sampleBloodGroup || ""),
        receptionRejectSampleReason: data[0]?.receptionRejectSampleReason
          ? {
              label: data[0].receptionRejectSampleReason,
              value: data[0].sampleBloodGroup?.receptionRejectSampleReason,
            }
          : null,
        service: data?.[0]?.patientHospitalization?.service || {},
      });

      // Fetch hospital's allowed blood bank for Forward Request destination
      if (data?.[0]?.hospital?._id) {
        try {
          const hospitalRes = await hospitalQuery(defaultLanguage, { id: data[0].hospital._id });
          const h = hospitalRes?.[0];
          if (h?.allowedBloodBank?._id) {
            this.setState({
              hospitalAllowedBloodBank: h.allowedBloodBank,
              hospitalAllowedBloodBankName: h.allowedBloodBank.name,
              hospitalMultipleAllowedBloodBanks: h.multipleAllowedBloodBank || [],
            });
          }
        } catch (_) {}
      }

      // Fetch requested products so Diagnosis is visible without opening the tab
      if (data?.[0]?._id) {
        try {
          const { data: productsData } = await fetchRequestedProducts(defaultLanguage, {
            page: 1,
            limit: 50,
            request: data[0]._id,
          });
          // Group by productType, then merge duplicates so the inventory-bearing
          // record (served) is primary but volume/diagnosis from the requested
          // record are not lost.
          const byKey = {};
          for (const item of productsData || []) {
            const key = item?.productType?._id;
            if (!key) continue;
            if (!byKey[key]) byKey[key] = [];
            byKey[key].push(item);
          }
          const merged = Object.values(byKey).map((records) => {
            if (records.length === 1) return records[0];
            const withInventory = records.find((r) => !!r.inventory);
            const withVolume = records.find((r) => !!r.volume);
            const primary =
              withInventory ||
              records.sort(
                (a, b) =>
                  new Date(b.updatedAt || b.createdAt) -
                  new Date(a.updatedAt || a.createdAt),
              )[0];
            if (withVolume && primary !== withVolume) {
              return {
                ...primary,
                volume: primary.volume || withVolume.volume,
                diagnosis: primary.diagnosis || withVolume.diagnosis,
                numberOfPackages:
                  primary.numberOfPackages || withVolume.numberOfPackages,
              };
            }
            return primary;
          });
          this.setState((prev) => ({
            productsDummy: merged,
            productsRefreshKey: prev.productsRefreshKey + 1,
          }));
        } catch (_) {}
      }

      // Fetch lab results for this patient
      if (data?.[0]?.patient?._id) {
        try {
          const labRes = await fetchPatientLabResult(this.props.defaultLanguage, {
            patient: data[0].patient._id,
          });
          this.setState({ labResults: Array.isArray(labRes) ? labRes : labRes?.data || [] });
        } catch (_) {}
      }
    } catch (error) {
      this.setState({
        isLoading: false,
      });
    }
  };

  handleShowModal(modal, modalTitle, selected_data = {}) {
    this.setState({
      [modal]: true,
      modalTitle: modalTitle,
      selected_data,
    });
  }

  handleCloseModal(modal) {
    this.setState({
      [modal]: false,
    });
  }

  renderInfoPlainText(key, value) {
    return (
      <div className="d-flex">
        <span style={{ marginRight: "0.3rem" }}>{key}: </span>{" "}
        <span>
          <b>{value}</b>
        </span>
      </div>
    );
  }

  renderProductRequested() {
    const requestedProducts = formatSelectData(
      this.state.data?.[0]?.products || [],
      "name",
      "_id",
    );
    return (
      <Select
        options={requestedProducts}
        label={language[this.props.defaultLanguage].requested_products}
        value={requestedProducts}
        isMulti
      />
    );
  }

  // Shown in two places — beside the status at the top of the request, and
  // beside the Sample Blood Group field — so the mismatch is visible whether
  // someone is skimming the header or working in the sample section.
  renderMismatchBadge = () => {
    const request = this.state.data?.[0];

    if (!request) return null;

    // Prefer what is currently selected over what is saved, so the badge is
    // right both while the lab is still choosing and after the confirm.
    const sampleGroup =
      this.state.bloodGroup?.value || request.sampleBloodGroup || "";

    const hasMismatch = this.isBloodGroupMismatch(sampleGroup);

    if (!hasMismatch) return null;

    return (
      <div
        className="badge badge-rejected"
        title={
          language[this.props.defaultLanguage].blood_group_mismatch_warning
        }
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: ".25rem",
          whiteSpace: "nowrap",
        }}
      >
        <span
          className="material-symbols-outlined"
          style={{ fontSize: "1rem" }}
        >
          error
        </span>
        {language[this.props.defaultLanguage].blood_group_mismatch}:{" "}
        {request.patient?.bloodGroup || "-"} → {sampleGroup || "-"}
      </div>
    );
  };

  // Back goes to the tab the request actually came from — the requests screen
  // shows bulk and nominative as tabs of /dashboard/requests, keyed on the
  // lowercased tab title (see components/Tabs).
  getRequestsListPath = () => {
    const type = (this.state.data?.[0]?.type || "").toLowerCase();

    if (type === "nominative") {
      return `/dashboard/requests?tab=${encodeURIComponent(
        "nominative requests",
      )}`;
    }

    if (type === "bulk") {
      return `/dashboard/requests?tab=${encodeURIComponent("bulk requests")}`;
    }

    return "/dashboard/requests";
  };

  // True only when BOTH groups are known and they differ. A patient with no
  // recorded blood group has nothing to compare against, so that is not a
  // mismatch — it would warn on every request for an unknown-group patient.
  isBloodGroupMismatch = (sampleGroup) => {
    const patientGroup = (
      this.state.data?.[0]?.patient?.bloodGroup || ""
    ).trim();
    const sample = (
      sampleGroup ??
      this.state.bloodGroup?.value ??
      this.state.data?.[0]?.sampleBloodGroup ??
      ""
    ).trim();

    if (!patientGroup || patientGroup.toLowerCase() === "unspecified" || !sample) return false;

    return patientGroup.toUpperCase() !== sample.toUpperCase();
  };

  // Parse a stored blood group string back into cascade fields (for Edit mode).
  parseSampleBloodGroup = (bg = "") => {
    const aboMatch = bg.match(/^(AB|A|B|O)/);
    const aboStr = aboMatch?.[1] || "";
    const abo = aboStr ? { label: aboStr, value: aboStr } : null;
    let rhesus = null, liss = null, iat = null, cc = null;
    if (abo) {
      if (bg.includes("Du+")) {
        rhesus = { label: "Negative", value: "Negative" };
        liss   = { label: "Negative", value: "Negative" };
        iat    = { label: "Du+",      value: "Du+"      };
      } else if (bg.includes("+")) {
        rhesus = { label: "Positive", value: "Positive" };
      } else if (bg.includes("-")) {
        rhesus = { label: "Negative", value: "Negative" };
        liss   = { label: "Negative", value: "Negative" };
        iat    = { label: "Negative", value: "Negative" };
        cc     = { label: "Positive", value: "Positive" };
      }
    }
    return { sampleAbo: abo, sampleRhesus: rhesus, sampleLiss: liss, sampleIat: iat, sampleCc: cc };
  };

  // Compute the final blood group from the current cascade state.
  computeSampleBloodGroup = (overrides = {}) => {
    const s = { ...this.state, ...overrides };
    const abo = s.sampleAbo?.value;
    if (!abo) { this.setState({ bloodGroup: null, ...overrides }); return; }
    const rh = (s.sampleRhesus?.value || "").toUpperCase();
    if (rh === "POSITIVE") {
      this.setState({ bloodGroup: { label: abo + "+", value: abo + "+" }, ...overrides }); return;
    }
    if (rh === "NEGATIVE") {
      const lv = (s.sampleLiss?.value || "").toUpperCase();
      if (lv === "POSITIVE") {
        this.setState({ bloodGroup: { label: abo + "+", value: abo + "+" }, ...overrides }); return;
      }
      if (lv === "NEGATIVE") {
        const iv = (s.sampleIat?.value || "").trim();
        if (iv === "Du+") {
          this.setState({ bloodGroup: { label: abo + "+ Du+", value: abo + "+ Du+" }, ...overrides }); return;
        }
        if (iv === "Negative" || iv === "d-") {
          const cv = (s.sampleCc?.value || "").toUpperCase();
          if (cv === "POSITIVE") {
            this.setState({ bloodGroup: { label: abo + "-", value: abo + "-" }, ...overrides }); return;
          }
          this.setState({ bloodGroup: null, ...overrides }); return;
        }
      }
    }
    this.setState({ bloodGroup: null, ...overrides });
  };

  // Gate on the Confirm button: sample blood group is mandatory, and a group
  // that disagrees with the patient's needs an explicit second confirmation.
  handleConfirmSample = async () => {
    const { error, sampleAbo, sampleRhesus, sampleLiss, sampleIat, sampleCc } = this.state;

    if (!sampleAbo) {
      error.sampleAbo = "Blood group (ABO) is required";
      this.setState({ error });
      toastMessage("error", "Blood group (ABO) is required");
      return;
    }

    if (!sampleRhesus) {
      error.sampleRhesus = "Rhesus (Rh D) is required";
      this.setState({ error });
      toastMessage("error", "Rhesus (Rh D) is required");
      return;
    }

    const rh = (sampleRhesus?.value || "").toUpperCase();
    if (rh === "NEGATIVE") {
      if (!sampleLiss) {
        error.sampleLiss = "Liss test is required when Rhesus is Negative";
        this.setState({ error });
        toastMessage("error", "Liss test is required when Rhesus is Negative");
        return;
      }
      const lv = (sampleLiss?.value || "").toUpperCase();
      if (lv === "NEGATIVE") {
        if (!sampleIat) {
          error.sampleIat = "IAT is required when Liss is Negative";
          this.setState({ error });
          toastMessage("error", "IAT is required when Liss is Negative");
          return;
        }
        const iv = (sampleIat?.value || "").trim();
        if (iv === "Negative" || iv === "d-") {
          if (!sampleCc) {
            error.sampleCc = "Check Cells (CC) is required when IAT is Negative";
            this.setState({ error });
            toastMessage("error", "Check Cells (CC) is required when IAT is Negative");
            return;
          }
          if (sampleCc?.value === "Negative") {
            toastMessage("error", "IAT result is invalid (CC Negative). Please repeat the IAT test.");
            return;
          }
        }
      }
    }

    const sampleGroup = this.state.bloodGroup?.value;
    if (!sampleGroup) {
      error.sampleAbo =
        language[this.props.defaultLanguage].sample_blood_group_required;
      this.setState({ error });
      toastMessage("error", language[this.props.defaultLanguage].sample_blood_group_required);
      return;
    }

    delete error.sampleAbo;
    delete error.sampleRhesus;
    delete error.sampleLiss;
    delete error.sampleIat;
    delete error.sampleCc;
    this.setState({ error });

    if (this.isBloodGroupMismatch(sampleGroup)) {
      this.setState({ showBloodGroupMismatch: true });
      return;
    }

    await this.handleUpdateRequest("tested");
    this.setState({ editingBloodGroup: false });
  };

  handleUpdateRequest = async (status, loader) => {
    try {
      if (status === "samplecollected" && !this.state.sampleCollectionDate) {
        toastMessage(
          "error",
          language[this.props.defaultLanguage].sample_collected_date_required,
        );
        return;
      }
      if (status === "samplecollected" && !this.state.sampleCollector) {
        toastMessage(
          "error",
          language[this.props.defaultLanguage].sample_collected_by_required,
        );
        return;
      }
      this.setState({ [loader || "isSubmitting"]: true });

      const { data, sampleCollectionDate, user } = this.state;

      const API_URL = process.env.REACT_APP_BBMS_BASE_API;

      let body = {
        id: data[0]._id,
        status,
      };

      if (status?.toUpperCase() === "SAMPLECOLLECTED") {
        body = {
          ...body,
          sampleCollectionDate,
          sampleCollectedBy:
            this.state.sampleCollector?._id ||
            this.state.sampleCollector?.id ||
            user.id,
        };
      }

      if (status?.toUpperCase() === "RECEIVED") {
        body = {
          ...body,
          sampleDecisionReception: "accepted",
          status: "received",
          receptionRejectSampleOtherReason: "",
          receptionRejectSampleReason: "",
        };
      }

      if (this.state.sampleDecisionReception?.toUpperCase() === "REJECTED") {
        body = {
          ...body,
          sampleDecisionReception: "rejected",
          receptionRejectSampleReason:
            this.state.receptionRejectSampleReason?.value,
          receptionRejectSampleOtherReason:
            this.state.receptionRejectSampleOtherReason,
          status: "received",
        };
      }

      if (status?.toUpperCase() === "TESTED") {
        body = {
          ...body,
          sampleBloodGroup: this.state.bloodGroup?.value,
          // Persisted so the mismatch stays visible to anyone opening this
          // request later, and remains on record that it was confirmed anyway.
          sampleBloodGroupMismatch: this.isBloodGroupMismatch(
            this.state.bloodGroup?.value,
          ),
        };
      }
      const options = {
        method: "PUT",
        url: `${API_URL}/bloodrequest`,
        data: body,
      };

      await api(options);

      // When the sample blood group is confirmed and the patient has no
      // recorded blood group, backfill it from the sample result.
      const patientBloodGroup = this.state.data[0]?.patient?.bloodGroup;
      const patientHasNoGroup =
        !patientBloodGroup ||
        patientBloodGroup.toLowerCase() === "unspecified";

      if (
        status?.toUpperCase() === "TESTED" &&
        this.state.bloodGroup?.value &&
        patientHasNoGroup
      ) {
        try {
          await api({
            method: "PUT",
            url: `${API_URL}/patient`,
            data: {
              id: this.state.data[0].patient._id,
              bloodGroup: this.state.bloodGroup.value,
            },
          });
        } catch (_) {}
      }

      this.setState({ [loader || "isSubmitting"]: false });

      toastMessage(
        "success",
        language[this.props.defaultLanguage].update_data_success,
      );

      this.getRequestInfo(true);
      this.setState({ editSampleCollected: false });
    } catch (error) {
      toastMessage("error", error);
      this.setState({ [loader || "isSubmitting"]: false });
    }
  };

  handleSubmitRequest = async () => {
    try {
      const { data } = this.state;

      this.setState({ isSubmitting: true });
      const API_URL = process.env.REACT_APP_BBMS_BASE_API;

      const options = {
        method: "POST",
        url: `${API_URL}/bloodrequest/process`,
        data: {
          requestId: data[0]._id,
        },
      };

      await api(options);
      this.getRequestInfo(true);
      this.setState({
        showSuspendModal: false,
        isSubmitting: false,
        showSubmitModal: false,
      });
    } catch (error) {
      toastMessage("error", error);
      this.setState({ isSubmitting: false });
    }
  };

  getEmergencyOptions = async () => {
    try {
      this.setState({ emergencyOptions: await fetchEmergencyOptions() });
    } catch (error) {
      /* the panels read without it; only the labels are poorer */
    }
  };

  render() {
    const request = this.state.data[0];
    const isEmergency = isEmergencyRelease(request);
    // No sample means no sample to collect, receive, group or crossmatch.
    // Not a step waived by choice but one that cannot be carried out, so
    // the screens do not offer it rather than leaving the laboratory to
    // work out which half of the page does not apply to them.
    const skipsSample = hasNoSample(request);

    const canEditBloodGroup = defineRole({
      roles: this.state.user.accessRole,
      menu: ROLE_LABORATORY,
      operation: "update",
    });

    // Lab staff can always correct the blood group. For others, lock once a
    // group is confirmed AND an inventory product is assigned.
    const bloodGroupLocked =
      !canEditBloodGroup &&
      !!this.state.data[0]?.sampleBloodGroup &&
      this.state.productsDummy.some((p) => !!p.inventory);

    const canAddSample = defineRole({
      roles: this.state.user.accessRole,
      menu: ROLE_SAMPLE_COLLECTED_RESULT,
      operation: "create",
    });

    const canAddProducts =
      defineRole({
        roles: this.state.user.accessRole,
        menu: ROLE_LABORATORY,
        operation: "create",
      }) ||
      defineRole({
        roles: this.state.user.accessRole,
        menu: ROLE_HOEMOVIGILANCE,
        operation: "create",
      });

    const canTransferProduct = defineRole({
      roles: this.state.user.accessRole,
      menu: ROLE_LABORATORY,
      operation: "create",
    });

    const canReviewSample = defineRole({
      roles: this.state.user.accessRole,
      menu: ROLE_LABORATORY,
      operation: "create,update",
    });

    const isSuperAdmin = this.state.user.role === "superAdmin";

    const canRequestProducts =
      (isSuperAdmin || ["initiated"].includes(this.state.data[0]?.status)) &&
      defineRole({
        roles: this.state.user.accessRole,
        menu: ROLE_NOMINATIVE_REQUEST,
        operation: "create",
      });

    const canEditRequestedProduct = defineRole({
      roles: this.state.user.accessRole,
      menu: ROLE_NOMINATIVE_REQUEST,
      operation: "update",
    });

    return (
      <>
        <div style={{ marginBottom: "1rem" }} />

        {this.state.isLoading ? (
          <Loading className="fullscreen" />
        ) : (
          <>
            <div style={{ display: "flex", alignItems: "center" }}>
              <div style={{ flex: 1, marginBottom: "1rem" }}>
                {this.state.data.length > 0 && (
                  <>
                    <div
                      className="mb-3"
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: ".5rem",
                        alignItems: "center",
                      }}
                    >
                      <div
                        className={`badge badge-${
                          ["initiated", "requestreceived"].includes(
                            this.state.data[0].status,
                          )
                            ? "info"
                            : this.state.data[0].status
                        }`}
                      >
                        {language[this.props.defaultLanguage].status}:{" "}
                        {capitalizeText(this.state.data[0].status)}
                      </div>

                      {this.renderMismatchBadge()}
                    </div>
                    <p>
                      {language[this.props.defaultLanguage].request_number}:{" "}
                      <b className="text-bold">
                        {this.state?.data[0]?.requestNo}
                      </b>
                    </p>
                    <p>
                      {language[this.props.defaultLanguage].BBMS_NUMBER}:{" "}
                      <b className="text-bold">
                        {this.state.data[0].patient?.patientBBMSNumber}
                      </b>
                    </p>
                    {this.state.data[0].shipmentNumber && (
                      <p className="text-primary">
                        {language[this.props.defaultLanguage].shipment_number}:{" "}
                        {this.state.data[0].shipmentNumber}
                      </p>
                    )}
                  </>
                )}
              </div>

              {/* Right-aligned, on the status indicator's line */}
              <div style={{ alignSelf: "flex-start", marginBottom: "1rem" }}>
                <Button
                  text={language[this.props.defaultLanguage].back}
                  className="btn-primary btn-sm"
                  type="link"
                  to={this.getRequestsListPath()}
                  icon="keyboard_backspace"
                />
              </div>
            </div>
            {this.state?.data?.length > 0 && (
              <>
                <div className="col-md-12">
                  <div className="card">
                    <div className="card-body">
                      <div>
                        <Separator
                          title="Patient Information"
                          titleClassName="text-primary"
                        />
                        <div className="row">
                          <div className="col-md-3">
                            <Input
                              value={`${this.state.data[0]?.patient?.firstName || "-"} ${
                                this.state.data[0]?.patient?.lastName || ""
                              }`.trim()}
                              label={
                                language[this.props.defaultLanguage].patient
                              }
                              disabled
                            />
                          </div>
                          <div className="col-md-3">
                            <Input
                              value={this.state.data[0]?.patient?.patientId || "-"}
                              label={
                                language[this.props.defaultLanguage].patient_id
                              }
                              disabled
                            />
                          </div>
                          <div className="col-md-3">
                            <Input
                              value={this.state.data[0]?.patient?.gender || "-"}
                              label={
                                language[this.props.defaultLanguage].gender
                              }
                              disabled
                            />
                          </div>
                          <div className="col-md-3">
                            <Input
                              value={this.state.data[0]?.patient?.dob || "-"}
                              label={
                                language[this.props.defaultLanguage]
                                  .date_of_birth
                              }
                              disabled
                            />
                          </div>
                          <div className="col-md-3">
                            <Input
                              value={this.state.data[0]?.patient?.bloodGroup || "-"}
                              label="Blood Group"
                              disabled
                            />
                          </div>
                          <div className="col-md-3">
                            <Input
                              value={
                                this.state.productsDummy?.find(
                                  (p) => p.diagnosis || p.otherDiagnosis
                                )?.diagnosis ||
                                this.state.productsDummy?.find(
                                  (p) => p.otherDiagnosis
                                )?.otherDiagnosis ||
                                "-"
                              }
                              label="Diagnosis"
                              disabled
                            />
                          </div>
                          {/* The booked time was not shown here at all, so a
                              doctor had no way to see it, let alone move it.

                              Only on a booking. Every nominative request used
                              to be stamped with a booking date when it was
                              created, so ordinary requests and emergency
                              releases showed a booked slot - and offered to
                              move it - for a time nobody had ever booked.
                              Checking the kind of request rather than the
                              presence of the date means the ones already
                              carrying a stray date stop showing it too. */}
                          {this.state.data[0]?.requestType === "isBooking" &&
                            this.state.data[0]?.bookingDate && (
                            <div className="col-md-3">
                              <BookingTimeEditor
                                request={this.state.data[0]}
                                onUpdated={() => this.getRequestInfo(false)}
                              />
                            </div>
                          )}
                        </div>
                        {this.state.data[0]?.patientHospitalization?._id && (
                          <>
                            <Separator
                              title="Patient Hospitalization"
                              titleClassName="text-primary"
                            />
                            <div className="row">
                              <div className="col-md-3">
                                <Input
                                  label={"Patient"}
                                  value={`${this.state.data[0]?.patientHospitalization?.patient?.firstName || "-"} ${
                                    this.state.data[0]?.patientHospitalization?.patient
                                      ?.lastName || ""
                                  }`.trim()}
                                  disabled
                                />
                              </div>
                              <div className="col-md-3">
                                <Input
                                  label={"Patient BBMS Number"}
                                  value={
                                    this.state.data[0]?.patientHospitalization
                                      ?.patient?.patientBBMSNumber || "-"
                                  }
                                  disabled
                                />
                              </div>
                              <div className="col-md-3">
                                <Input
                                  label={"Hospital"}
                                  value={
                                    this.state.data[0]?.patientHospitalization
                                      ?.hospital?.name || "-"
                                  }
                                  disabled
                                />
                              </div>
                              <div className="col-md-3">
                                <Input
                                  value={
                                    this.state.data[0]?.patientHospitalization
                                      ?.service?.name || "-"
                                  }
                                  label={"Service"}
                                  disabled
                                />
                              </div>
                              <div className="col-md-3">
                                <Input
                                  value={
                                    this.state.data[0]?.patientHospitalization
                                      ?.roomNumber || "-"
                                  }
                                  label={
                                    language[this.props.defaultLanguage]
                                      .room_number
                                  }
                                  disabled
                                />
                              </div>
                              <div className="col-md-3">
                                <Input
                                  value={
                                    this.state.data[0]?.patientHospitalization
                                      ?.bedNumber || "-"
                                  }
                                  label={
                                    language[this.props.defaultLanguage]
                                      .bed_number
                                  }
                                  disabled
                                />
                              </div>
                              <div className="col-md-3">
                                <Input
                                  label={"Hospitalization Status"}
                                  value={
                                    this.state.data[0]?.patientHospitalization
                                      ?.isActive
                                      ? "Active"
                                      : "Inactive"
                                  }
                                  disabled
                                />
                              </div>
                            </div>
                          </>
                        )}
                        <Separator
                          title="Request Information"
                          titleClassName="text-primary"
                        />
                        <div className="row">
                          <div className="col-md-3">
                            <Input
                              value={
                                this.state.data[0].consent
                                  ? language[this.props.defaultLanguage].yes
                                  : language[this.props.defaultLanguage].no
                              }
                              label={
                                language[this.props.defaultLanguage].consented
                              }
                              disabled
                            />
                          </div>
                          {this.state.data[0].commitment && (
                            <div className="col-md-3">
                              <Input
                                value={this.state.data[0].commitment}
                                label={
                                  language[this.props.defaultLanguage]
                                    .commitment
                                }
                                disabled
                                textarea
                              />
                            </div>
                          )}

                          <div className="col-md-3">
                            <Input
                              value={
                                this.state.data[0].requestType === "isBooking"
                                  ? language[this.props.defaultLanguage].booking
                                  : this.state.data[0].requestType === "isEmergencyRelease"
                                  ? language[this.props.defaultLanguage].emergency_release
                                  : language[this.props.defaultLanguage].ordinary
                              }
                              label={
                                language[this.props.defaultLanguage].request_type
                              }
                              disabled
                            />
                          </div>
                          {this.state.data[0].requestType === "isBooking" &&
                            this.state.data[0].bookingDate && (
                              <div className="col-md-3">
                                <Input
                                  value={this.state.data[0].bookingDate}
                                  label={
                                    language[this.props.defaultLanguage]
                                      .booking_date
                                  }
                                  disabled
                                />
                              </div>
                            )}
                          <div className="col-md-3">
                            <Input
                              value={this.state.data[0].account?.name}
                              label={
                                language[this.props.defaultLanguage]
                                  .requested_by
                              }
                              disabled
                            />
                          </div>
                          <div className="col-md-3">
                            <Input
                              value={this.state.data[0]?.requestingDoctorContacts || this.state.data[0]?.account?.phone || "-"}
                              label="Requestor Phone Number"
                              disabled
                            />
                          </div>
                          <div className="col-md-3">
                            <Input
                              value={this.state.data[0]?.requestingDoctorEmail || this.state.data[0]?.account?.email || "-"}
                              label="Requestor Email"
                              disabled
                            />
                          </div>
                          <div className="col-md-3">
                            {this.state.sampleCollected?.bloodGroup && (
                              <h1
                                style={{
                                  color: "var(--primary-color)",
                                  fontSize: 60,
                                }}
                              >
                                {this.state.sampleCollected?.bloodGroup}
                              </h1>
                            )}
                          </div>
                        </div>
                        <Separator
                          title="Requested Products"
                          titleClassName="text-primary"
                        />
                        <div className="row">
                          <div className="col-md-12">
                            <table className="table table-sm table-bordered">
                              <thead>
                                <tr>
                                  <th>Product</th>
                                  <th>Volume (ml)</th>
                                  <th>Number of Packages</th>
                                  <th>Lab Results</th>
                                  {canEditRequestedProduct && <th></th>}
                                </tr>
                              </thead>
                              <tbody>
                                {(this.state.productsDummy || []).length === 0 ? (
                                  <tr>
                                    <td colSpan={canEditRequestedProduct ? 5 : 4} className="text-center">-</td>
                                  </tr>
                                ) : (
                                  this.state.productsDummy.map((p, i) => {
                                    const name = (p.productType?.name || "").toLowerCase();
                                    const isRBC = name.includes("red blood") || name.includes("rbc");
                                    const isPlatelet = name.includes("platelet");
                                    const getResult = (exam) =>
                                      this.state.labResults.find(
                                        (r) => r.exam?.toLowerCase() === exam.toLowerCase()
                                      )?.result || null;

                                    const stripUnit = (v, unit) =>
                                      v ? v.toString().replace(new RegExp(`\\s*${unit.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s*$`, "i"), "").trim() : v;

                                    let labDisplay = "-";
                                    if (isRBC) {
                                      const hgb = getResult("Hgb");
                                      const hct = getResult("HCT");
                                      labDisplay = [
                                        hgb ? `HGB: ${stripUnit(hgb, "g/dL")} g/dL` : null,
                                        hct ? `HCT: ${stripUnit(hct, "%")}%` : null,
                                      ].filter(Boolean).join(" | ") || "-";
                                    } else if (isPlatelet) {
                                      const plt = getResult("PLT Count");
                                      labDisplay = plt ? `PLT: ${stripUnit(plt, "×10³/µL")} ×10³/µL` : "-";
                                    }

                                    return (
                                      <tr key={i}>
                                        <td>{p.productType?.name || "-"}</td>
                                        <td>{p.volume != null && p.volume !== "" ? `${p.volume} ml` : "-"}</td>
                                        <td>{p.numberOfPackages || "-"}</td>
                                        <td>{labDisplay}</td>
                                        {canEditRequestedProduct && (
                                          <td>
                                            <button
                                              className="btn btn-sm btn-danger"
                                              onClick={() =>
                                                this.setState({ selectedData: p, showToConfirm: true })
                                              }
                                            >
                                              {language[this.props.defaultLanguage].edit}
                                            </button>
                                          </td>
                                        )}
                                      </tr>
                                    );
                                  })
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                        {isEmergency && (
                          <>
                            <Endorsement
                              request={request}
                              options={this.state.emergencyOptions}
                              onEndorsed={() => this.getRequestInfo(true)}
                            />
                            <PostReleaseResults
                              request={request}
                              onSaved={() => this.getRequestInfo(true)}
                            />
                          </>
                        )}

                        {skipsSample && (
                          <div className="col-md-12">
                            <p className="text-muted">
                              {
                                language[this.props.defaultLanguage]
                                  .emergency_no_sample_note
                              }
                            </p>
                          </div>
                        )}

                        {!skipsSample && (
                        <>
                        <Separator
                          title="Sample Collection"
                          titleClassName="text-primary"
                        />
                        <div className="row align-items-end">
                          <div className="col-md-3">
                            <Input
                              value={this.state.sampleCollectionDate}
                              label={language[this.props.defaultLanguage].date}
                              disabled={
                                !canAddSample ||
                                (!!this.state.data[0].sampleCollectedBy &&
                                  !!this.state.sampleCollectionDate &&
                                  !this.state.editSampleCollected)
                              }
                              type="datetime-local"
                              onChange={(e) =>
                                this.onChangeText("sampleCollectionDate", e)
                              }
                              error={this.state.error.sampleCollectionDate}
                              // min={moment(this.state.data[0].createdAt).format(
                              //   "YYYY-MM-DD HH:mm"
                              // )}
                              // max={moment().format("YYYY-MM-DD HH:mm")}
                            />
                          </div>

                          {canAddSample &&
                            (!this.state.data[0].sampleCollectedBy ||
                              !!this.state.editSampleCollected) && (
                              <div className="col-md-3">
                                <Select
                                  loadOptions={(e) =>
                                    this.getAccounts(true, { q: e })
                                  }
                                  placeholder={
                                    language[this.props.defaultLanguage].select
                                  }
                                  label={
                                    language[this.props.defaultLanguage]
                                      .sample_collected_by
                                  }
                                  value={this.state.sampleCollector}
                                  onChange={(e) =>
                                    this.onChangeText("sampleCollector", e)
                                  }
                                  error={this.state.error.sampleCollector}
                                  isLoading={this.state.isFetchingAccount}
                                />
                              </div>
                            )}

                          {/* display sample collector */}
                          {!!this.state.data[0].sampleCollectedBy &&
                            !this.state.editSampleCollected && (
                              <div className="col-md-3">
                                <Input
                                  value={
                                    this.state.data[0].sampleCollectedBy?.name
                                  }
                                  label={
                                    language[this.props.defaultLanguage]
                                      .sample_collected_by
                                  }
                                  disabled
                                />
                              </div>
                            )}
                          {this.state.data[0].status === "samplecollected" &&
                            canAddSample && (
                              <div className="col-md-2 pb-3">
                                <Button
                                  text={
                                    this.state.editSampleCollected
                                      ? language[this.props.defaultLanguage]
                                          .cancel
                                      : language[this.props.defaultLanguage]
                                          .edit
                                  }
                                  onPress={this.onEditSampleCollected.bind(
                                    this,
                                  )}
                                />
                              </div>
                            )}
                        </div>
                        {canAddSample &&
                          (this.state.data[0].status === "pending" ||
                            this.state.data[0].status === "initiated" ||
                            !this.state.sampleCollectionDate ||
                            !!this.state.editSampleCollected) && (
                            <div>
                              <Button
                                text={language[this.props.defaultLanguage].save}
                                onPress={this.handleUpdateRequest.bind(
                                  this,
                                  "samplecollected",
                                )}
                                isSubmitting={this.state.isSubmitting}
                              />
                            </div>
                          )}
                        {
                          <>
                            <div className="row">
                              {this.state.data[0]
                                .receptionSampleCollectionDate && (
                                <>
                                  <Separator
                                    title="Sample Reception"
                                    titleClassName="text-primary"
                                  />
                                  <div className="col-md-3">
                                    <Input
                                      value={
                                        this.state.receptionSampleCollectionDate
                                          ? moment(
                                              this.state
                                                .receptionSampleCollectionDate,
                                            ).format("lll")
                                          : ""
                                      }
                                      label={
                                        language[this.props.defaultLanguage]
                                          .date
                                      }
                                      disabled
                                    />
                                  </div>
                                </>
                              )}

                              {this.state.data[0]?.status ===
                                "samplecollected" &&
                                canReviewSample && (
                                  <div className="d-flex align-items-center gap-3">
                                    <p>
                                      Do both the request and the sample meet
                                      the required standards?
                                    </p>
                                    <div className="d-flex align-items-center  gap-3">
                                      <Button
                                        text={
                                          language[this.props.defaultLanguage]
                                            .yes
                                        }
                                        onPress={() => {
                                          this.setState({
                                            sampleDecisionReception: "accepted",
                                            receptionRejectSampleReason: null,
                                            receptionRejectSampleOtherReason:
                                              "",
                                          });
                                          this.handleUpdateRequest(
                                            "requestreceived",
                                            "isSubmittingReceived",
                                          );
                                        }}
                                        isSubmitting={
                                          this.state.isSubmittingReceived
                                        }
                                      />
                                      <Button
                                        className="btn-default"
                                        text={
                                          language[this.props.defaultLanguage]
                                            .no
                                        }
                                        onPress={() =>
                                          this.setState({
                                            sampleDecisionReception: "rejected",
                                          })
                                        }
                                        isSubmitting={
                                          this.state.isSubmittingRejected
                                        }
                                      />
                                    </div>
                                  </div>
                                )}

                              {this.state.sampleDecisionReception ===
                                "rejected" && (
                                <div className="col-md-3">
                                  <Select
                                    options={[
                                      {
                                        label: "Insufficient",
                                        value: "INSUFFICIENT",
                                      },
                                      {
                                        label: "Hemolyzed",
                                        value: "HEMOLYZED",
                                      },

                                      {
                                        label: "Patient ID mismatch",
                                        value: "PATIENT_ID_MISMATCH",
                                      },
                                      {
                                        label: "Other",
                                        value: "OTHER",
                                      },
                                    ]}
                                    value={
                                      this.state.receptionRejectSampleReason
                                    }
                                    label={
                                      language[this.props.defaultLanguage]
                                        .reason
                                    }
                                    onChange={(e) =>
                                      this.onChangeText(
                                        "receptionRejectSampleReason",
                                        e,
                                      )
                                    }
                                    error={
                                      this.state.error
                                        .receptionRejectSampleReason
                                    }
                                    disabled={
                                      !canAddProducts ||
                                      this.state.data[0].status?.toUpperCase() !==
                                        "SAMPLECOLLECTED"
                                    }
                                  />
                                </div>
                              )}

                              {this.state.receptionRejectSampleReason?.value ===
                                "OTHER" && (
                                <div className="col-md-3">
                                  <Input
                                    value={
                                      this.state
                                        .receptionRejectSampleOtherReason
                                    }
                                    label={
                                      language[this.props.defaultLanguage]
                                        .please_specify
                                    }
                                    disabled={
                                      this.state.receptionRejectSampleReason
                                        ?.value !== "OTHER"
                                    }
                                    onChange={(e) =>
                                      this.onChangeText(
                                        "receptionRejectSampleOtherReason",
                                        e,
                                      )
                                    }
                                    error={
                                      this.state.error
                                        .receptionRejectSampleOtherReason
                                    }
                                    texarea
                                  />
                                </div>
                              )}

                              {/* display sample collector */}
                              {!!this.state.data[0].sampleReceivedBy && (
                                <div className="col-md-3">
                                  <Input
                                    value={
                                      this.state.data[0].sampleReceivedBy?.name
                                    }
                                    label={
                                      language[this.props.defaultLanguage]
                                        .received_by
                                    }
                                    disabled
                                  />
                                </div>
                              )}

                              {canAddProducts &&
                                this.state.data[0]?.status?.toUpperCase() ===
                                  "SAMPLECOLLECTED" &&
                                this.state.receptionRejectSampleReason &&
                                this.state.sampleDecisionReception?.toUpperCase() ===
                                  "REJECTED" && (
                                  <div>
                                    <Button
                                      text={
                                        language[this.props.defaultLanguage]
                                          .submit
                                      }
                                      onPress={this.handleUpdateRequest.bind(
                                        this,
                                        "received",
                                      )}
                                      isSubmitting={this.state.isSubmitting}
                                    />
                                  </div>
                                )}
                            </div>
                            {canAddProducts &&
                              ["tested", "requestreceived"].includes(
                                this.state.data[0].status,
                              ) && (
                                <>
                                  {(() => {
                                    const isDirectEntry =
                                      canEditBloodGroup &&
                                      !this.state.data[0]?.sampleBloodGroup &&
                                      !bloodGroupLocked;
                                    const bloodGroupEditable =
                                      !bloodGroupLocked &&
                                      (this.state.editingBloodGroup || isDirectEntry);

                                    const { sampleAbo, sampleRhesus, sampleLiss, sampleIat, sampleCc } = this.state;
                                    const rhNeg = sampleRhesus?.value === "Negative";
                                    const lissNeg = sampleLiss?.value === "Negative";
                                    const iatNeg = sampleIat?.value === "Negative" || sampleIat?.value === "d-";
                                    const ccNegWarn = iatNeg && sampleCc?.value === "Negative";

                                    const ABOOptions = [
                                      { label: "A", value: "A" },
                                      { label: "B", value: "B" },
                                      { label: "AB", value: "AB" },
                                      { label: "O", value: "O" },
                                    ];
                                    const RhOptions = [
                                      { label: "Positive", value: "Positive" },
                                      { label: "Negative", value: "Negative" },
                                    ];
                                    const LissIatCcOptions = [
                                      { label: "Positive", value: "Positive" },
                                      { label: "Negative", value: "Negative" },
                                    ];
                                    const IatOptions = [
                                      { label: "Du+",      value: "Du+"      },
                                      { label: "Negative", value: "Negative" },
                                    ];

                                    return (
                                      <div className="col-md-12">
                                        {/* Cascade selects row */}
                                        <div className="d-flex align-items-end gap-2 flex-wrap">
                                          {/* ABO */}
                                          <div style={{ minWidth: 110 }}>
                                            <Select
                                              options={ABOOptions}
                                              value={sampleAbo}
                                              label={language[this.props.defaultLanguage].sample_blood_group}
                                              required
                                              disabled={!bloodGroupEditable}
                                              onChange={(e) => {
                                                this.computeSampleBloodGroup({
                                                  sampleAbo: e,
                                                  sampleRhesus: null,
                                                  sampleLiss: null,
                                                  sampleIat: null,
                                                  sampleCc: null,
                                                });
                                              }}
                                              error={this.state.error.sampleAbo}
                                              helperText="ABO blood group of the patient's sample."
                                            />
                                          </div>

                                          {/* Rhesus — appears once ABO is picked */}
                                          {sampleAbo && (
                                            <div style={{ minWidth: 120 }}>
                                              <Select
                                                options={RhOptions}
                                                value={sampleRhesus}
                                                label="Rhesus (Rh D)"
                                                required
                                                disabled={!bloodGroupEditable}
                                                onChange={(e) => {
                                                  this.computeSampleBloodGroup({
                                                    sampleRhesus: e,
                                                    sampleLiss: null,
                                                    sampleIat: null,
                                                    sampleCc: null,
                                                  });
                                                }}
                                                error={this.state.error.sampleRhesus}
                                                helperText="Positive if D antigen is present. Negative requires Liss test."
                                              />
                                            </div>
                                          )}

                                          {/* Liss — mandatory when Rhesus = Negative */}
                                          {rhNeg && (
                                            <div style={{ minWidth: 110 }}>
                                              <Select
                                                options={LissIatCcOptions}
                                                value={sampleLiss}
                                                label="Liss/Incubation at 37°C"
                                                required
                                                disabled={!bloodGroupEditable}
                                                onChange={(e) => {
                                                  this.computeSampleBloodGroup({
                                                    sampleLiss: e,
                                                    sampleIat: null,
                                                    sampleCc: null,
                                                  });
                                                }}
                                                error={this.state.error.sampleLiss}
                                                helperText="Incubate at 37°C. Positive → patient is D+. Negative → proceed to IAT."
                                              />
                                            </div>
                                          )}

                                          {/* IAT — mandatory when Liss = Negative */}
                                          {lissNeg && (
                                            <div style={{ minWidth: 130 }}>
                                              <Select
                                                options={IatOptions}
                                                value={sampleIat}
                                                label="Indirect Antiglobulin Test - IAT (Du+/d-)"
                                                required
                                                disabled={!bloodGroupEditable}
                                                onChange={(e) => {
                                                  this.computeSampleBloodGroup({
                                                    sampleIat: e,
                                                    sampleCc: null,
                                                  });
                                                }}
                                                error={this.state.error.sampleIat}
                                                helperText="Du+ = weak D antigen detected → D+. Negative → proceed to Check Cells."
                                              />
                                            </div>
                                          )}

                                          {/* CC — mandatory when IAT = Negative */}
                                          {iatNeg && (
                                            <div style={{ minWidth: 130 }}>
                                              <Select
                                                options={LissIatCcOptions}
                                                value={sampleCc}
                                                label="Check Cells (CC)"
                                                required
                                                disabled={!bloodGroupEditable}
                                                onChange={(e) => {
                                                  this.computeSampleBloodGroup({ sampleCc: e });
                                                }}
                                                error={this.state.error.sampleCc}
                                                helperText="Positive confirms D− (blood group gets −). Negative = IAT invalid, repeat required."
                                              />
                                            </div>
                                          )}

                                          {/* CC Negative warning — inline, next to CC select */}
                                          {ccNegWarn && (
                                            <div className="mb-2" style={{ fontSize: 12, color: "#856404", backgroundColor: "#fff3cd", border: "1px solid #ffc107", padding: "4px 10px", borderRadius: 4, alignSelf: "flex-end" }}>
                                              IAT result is <strong>invalid</strong> (CC Negative).<br />Please repeat the IAT test.
                                            </div>
                                          )}

                                          {/* Computed blood group result */}
                                          {this.state.bloodGroup?.value && (
                                            <div className="mb-2" style={{ paddingBottom: 2 }}>
                                              <strong style={{ fontSize: 22, color: "#5c1e1e" }}>
                                                {(() => {
                                                  const val = this.state.bloodGroup.value;
                                                  const duIdx = val.indexOf(" Du+");
                                                  if (duIdx === -1) return val;
                                                  return (
                                                    <>
                                                      {val.slice(0, duIdx)}
                                                      <sup style={{ fontSize: "0.55em" }}>Du+</sup>
                                                    </>
                                                  );
                                                })()}
                                              </strong>
                                            </div>
                                          )}

                                          {this.renderMismatchBadge() && (
                                            <div className="mb-2">
                                              {this.renderMismatchBadge()}
                                            </div>
                                          )}

                                          {/* Edit button */}
                                          {!bloodGroupLocked &&
                                            !this.state.editingBloodGroup &&
                                            !isDirectEntry && (
                                              <div className="mb-2">
                                                <Button
                                                  className="btn-sm btn-danger"
                                                  icon={icons.edit}
                                                  text={language[this.props.defaultLanguage].edit}
                                                  onPress={() => {
                                                    const parsed = this.parseSampleBloodGroup(
                                                      this.state.data[0]?.sampleBloodGroup || ""
                                                    );
                                                    this.setState({ editingBloodGroup: true, ...parsed });
                                                  }}
                                                />
                                              </div>
                                            )}

                                          {/* Confirm / Cancel */}
                                          {!bloodGroupLocked && bloodGroupEditable && (
                                            <div className="d-flex gap-2 mb-2">
                                              {this.state.editingBloodGroup && (
                                                <Button
                                                  className="btn-default"
                                                  text={language[this.props.defaultLanguage].cancel}
                                                  onPress={() => {
                                                    const parsed = this.parseSampleBloodGroup(
                                                      this.state.data[0]?.sampleBloodGroup || ""
                                                    );
                                                    this.setState({
                                                      editingBloodGroup: false,
                                                      error: {},
                                                      bloodGroup: this.state.data[0]?.sampleBloodGroup
                                                        ? { label: this.state.data[0].sampleBloodGroup, value: this.state.data[0].sampleBloodGroup }
                                                        : null,
                                                      ...parsed,
                                                    });
                                                  }}
                                                />
                                              )}
                                              <Button
                                                text={language[this.props.defaultLanguage].confirm}
                                                onPress={this.handleConfirmSample.bind(this)}
                                                disabled={!this.state.bloodGroup}
                                                isSubmitting={this.state.isSubmitting}
                                              />
                                            </div>
                                          )}
                                        </div>


                                      </div>
                                    );
                                  })()}
                                </>
                              )}
                          </>
                        }
                        </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <br />

                <div className="col-md-12">
                  <div className="card">
                    <ControlledTabs
                      /* A laboratory arriving from a booking prompt is sent
                         straight to the tab it has to act on, rather than
                         landing on the first one and hunting. */
                      activeTab={
                        new URLSearchParams(window.location.search).get("tab") ||
                        undefined
                      }
                      options={[
                        this.state.data[0].shipmentNumber && {
                          title:
                            language[this.props.defaultLanguage]
                              .served_products,
                          data: (
                            <ServedProducts
                              shipmentNumber={this.state.data[0].shipmentNumber}
                            />
                          ),
                        },
                        canTransferProduct &&
                          // A no-sample emergency release has no sample
                          // collection date and never reaches "tested" -
                          // both of those come from working on a sample that
                          // does not exist. Without this the laboratory can
                          // see the request and still has no way to issue
                          // against it, which is the one thing it needs to
                          // do.
                          (skipsSample ||
                            !!this.state.data[0].sampleCollectionDate) &&
                          ([
                            "tested",
                            "processing",
                            "received",
                            ...(skipsSample ? ["requestreceived"] : []),
                          ].includes(this.state.data[0]?.status)) && {
                            title:
                              language[this.props.defaultLanguage]
                                .transfer_product,
                            data: (
                              <div className="col-md-12">
                                <DirectTransfer
                                  from={{
                                    label: this.state.user?.stockName,
                                    value: this.state.user?.stock,
                                  }}
                                  to={{
                                    label: this.state.user?.stockName,
                                    value: this.state.user?.stock,
                                  }}
                                  requestNo={this.state.data[0]?.requestNo}
                                  requestId={this.state.data[0]._id}
                                  patientId={this.state.data[0]?.patient?._id}
                                  patientName={`${this.state.data[0]?.patient?.firstName || ""} ${this.state.data[0]?.patient?.lastName || ""}`.trim()}
                                  shipmentNumber={
                                    this.state.data[0].shipmentNumber
                                  }
                                  sampleNotCollected={
                                    !this.state.data[0].sampleCollectionDate
                                  }
                                  isForNominative
                                  request={this.state.data[0]}
                                  hospital={this.state.data[0]?.hospital}
                                  service={this.state.service}
                                />
                              </div>
                              // <ControlledTabs
                              //   options={[
                              //     {
                              //       title:
                              //         language[this.props.defaultLanguage]
                              //           .new_record,
                              //       data: <div>ksdjksdjsd</div>,
                              //     },
                              //     this.state.data[0].shipmentNumber && {
                              //       title:
                              //         language[this.props.defaultLanguage]
                              //           .served_products,
                              //       data: (
                              //         <ServedProducts
                              //           shipmentNumber={
                              //             this.state.data[0].shipmentNumber
                              //           }
                              //         />
                              //       ),
                              //     },
                              //   ].filter(Boolean)}
                              // />
                            ),
                          },
                        {
                          title:
                            language[this.props.defaultLanguage]
                              .request_product,
                          data: (
                            <>
                              {canRequestProducts && (
                                <div className="card-header">
                                  <div className="d-flex gap-2 justify-content-end">
                                    <Button
                                      text={
                                        language[this.props.defaultLanguage]
                                          .request_product
                                      }
                                      // isSubmitting={this.state.isSubmitting}
                                      onPress={() => {
                                        this.setState({ selectedData: null });
                                        this.handleShowModal("showToConfirm");
                                      }}
                                      icon={icons.add}
                                    />
                                    {!!(this.state.productsDummy || [])
                                      .length && (
                                      <>
                                        <Button
                                          text={
                                            language[this.props.defaultLanguage]
                                              .submit_request
                                          }
                                          // isSubmitting={this.state.isSubmitting}
                                          onPress={() =>
                                            this.handleShowModal(
                                              "showSubmitModal",
                                            )
                                          }
                                          icon={icons.success}
                                        />
                                      </>
                                    )}
                                  </div>
                                </div>
                              )}
                              <Products
                                refreshKey={this.state.productsRefreshKey}
                                from={this.state.data[0]?.hospital}
                                to={this.state.user.allowedBloodBank}
                                allowedBloodBankName={
                                  this.state.user.allowedBloodBankName
                                }
                                requestNo={this.state.data[0].requestNo}
                                products={this.state.data[0].products || []}
                                requestId={this.state.data[0]._id}
                                requestStatus={this.state.data[0].status}
                                patientId={this.state.data?.[0].patient?._id}
                                requestingDoctorName={this.state.data[0]?.requestingDoctorName}
                                requestingDoctorContacts={this.state.data[0]?.requestingDoctorContacts}
                                requestingDoctorEmail={this.state.data[0]?.requestingDoctorEmail}
                                sampleCollected={this.state.sampleCollected}
                                shipmentNumber={this.data?.[0].shipmentNumber}
                                handleRefresh={(b) => {
                                  if (b) {
                                    this.getRequestInfo(true);
                                  }
                                }}
                                onLoadProducts={(products) =>
                                  this.setState({ productsDummy: products })
                                }
                                hideTitle
                                isForNominative
                              />
                            </>
                          ),
                        },
                        {
                          title:
                            language[this.props.defaultLanguage].lab_results,
                          data: (
                            <div className="card-body">
                              <LabResults
                                hospitalId={this.state.data?.[0].hospital?._id}
                                patientId={this.state.data?.[0].patient?._id}
                                isFromRequest
                                patientBBMSNumber={
                                  this.state.data?.[0].patient
                                    ?.patientBBMSNumber
                                }
                                patientName={`${this.state.data?.[0]?.patient?.firstName || ""} ${this.state.data?.[0]?.patient?.lastName || ""}`.trim()}
                              />
                            </div>
                          ),
                        },
                        {
                          title: language[this.props.defaultLanguage].forwarded,
                          data: (
                            <div className="card-body">
                              <ForwardedList
                                requestId={this.state.data?.[0]?._id}
                                from={this.state.data?.[0]?.hospital}
                                to={this.state.hospitalAllowedBloodBank || this.state.user?.allowedBloodBank || this.state.data?.[0]?.bloodBank}
                                allowedBloodBankName={this.state.hospitalAllowedBloodBankName || this.state.user?.allowedBloodBankName || this.state.data?.[0]?.bloodBank?.name}
                                multipleAllowedBloodBanks={this.state.hospitalMultipleAllowedBloodBanks || []}
                                requestNo={this.state.data?.[0]?.requestNo}
                                requestingDoctorName={this.state.data?.[0]?.requestingDoctorName}
                                requestingDoctorContacts={this.state.data?.[0]?.requestingDoctorContacts}
                                requestingDoctorEmail={this.state.data?.[0]?.requestingDoctorEmail}
                                requestedByAccount={this.state.data?.[0]?.account}
                                sampleBloodGroup={this.state.data?.[0]?.sampleBloodGroup}
                                requestedProductsCount={this.state.productsDummy.length}
                              />
                            </div>
                          ),
                        },
                      ].filter(Boolean)}
                    />
                  </div>
                </div>
              </>
            )}
          </>
        )}

        <Modal
          show={this.state.showBloodGroupMismatch}
          handleClose={() => this.handleCloseModal("showBloodGroupMismatch")}
        >
          <div className="card" style={{ margin: 0 }}>
            <div className="card-body">
              <div className="confirmation-modal-container">
                {/* Icon + title */}
                <div className="confirmation-modal-header">
                  <div className="confirmation-modal-icon-container danger">
                    <span className="material-symbols-outlined">warning</span>
                  </div>
                </div>

                <center className="confirmation-modal-content">
                  <h2>{language[this.props.defaultLanguage].blood_group_mismatch_title}</h2>
                  <p>{language[this.props.defaultLanguage].blood_group_mismatch_description}</p>
                </center>

                {/* Blood group comparison — side by side */}
                <div className="row" style={{ margin: "1rem 0 0.5rem" }}>
                  <div className="col-md-5">
                    <div className="card">
                      <div className="card-body" style={{ textAlign: "center" }}>
                        <p className="text-disabled" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>
                          {language[this.props.defaultLanguage].patient_blood_group}
                        </p>
                        <h3 className="text-bold" style={{ margin: 0 }}>
                          {this.state.data?.[0]?.patient?.bloodGroup || "—"}
                        </h3>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-2 d-flex align-items-center justify-content-center">
                    <span className="material-symbols-outlined text-disabled">arrow_forward</span>
                  </div>

                  <div className="col-md-5">
                    <div className="card" style={{ borderColor: "var(--primary-color, #5c1e1e)" }}>
                      <div className="card-body" style={{ textAlign: "center" }}>
                        <p className="text-disabled" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>
                          {language[this.props.defaultLanguage].sample_blood_group}
                        </p>
                        <h3 className="text-bold text-primary" style={{ margin: 0 }}>
                          {this.state.bloodGroup?.value || "—"}
                        </h3>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <Button
                text={language[this.props.defaultLanguage].no}
                className="btn-default border"
                onPress={this.handleCloseModal.bind(this, "showBloodGroupMismatch")}
              />
              <Button
                text={language[this.props.defaultLanguage].yes}
                className="btn-primary"
                onPress={async () => {
                  this.handleCloseModal("showBloodGroupMismatch");
                  await this.handleUpdateRequest("tested");
                  this.setState({ editingBloodGroup: false });
                }}
                isSubmitting={this.state.isSubmitting}
              />
            </div>
          </div>
        </Modal>

        <Modal
          show={this.state.showToConfirm}
          title={language[this.props.defaultLanguage].request_product}
          handleClose={() => this.handleCloseModal("showToConfirm")}
          size="lg"
        >
          <NewRequest
            handleClose={() => this.handleCloseModal("showToConfirm")}
            {...this.props}
            request={this.state?.data[0]?._id}
            getData={this.getRequestInfo.bind(this, true)}
            selectedData={this.state.selectedData || []}
            data={this.state.data?.[0]?.products}
            isForNominative
          />
        </Modal>

        <Modal
          handleClose={this.handleCloseModal.bind(this, "showSubmitModal")}
          show={this.state.showSubmitModal}
          title={language[this.props.defaultLanguage].submit_request}
        >
          <ConfirmationModal
            handleCloseModal={this.handleCloseModal.bind(
              this,
              "showSubmitModal",
            )}
            error={this.state.error}
            onPressNegative={this.handleCloseModal.bind(
              this,
              "showSuspendModal",
            )}
            onPressPositive={() =>
              this.handleSubmitRequest("pending", "showSubmitModal")
            }
            // onChange={(e) => this.onChangeText("reason", e)}
            isSubmitting={this.state.isSubmitting}
            title="Submit request to the Nurse"
            description="Once the request is submitted the Nurse will be able to process it."
            withReason={false}
            // reason={this.state?.blockInfo?.blockReason || this.state.reason}
            {...this.props}
            // reasonFieldName="reason"
            negativeButtonText={language[this.props.defaultLanguage].cancel}
            positiveButtonText={language[this.props.defaultLanguage].submit}
            isPositiveSubmitting={this.state.isSubmitting}
          />
        </Modal>
      </>
    );
  }
}

const mapStateToProps = (state) => {
  const { defaultLanguage } = state.Language;
  return {
    defaultLanguage,
  };
};

export default connect(mapStateToProps)(RequestInfo);
